import { SolveResponse } from '../../types/tutor';

export const MOCK_SOLVE_RESPONSE: SolveResponse = {
  problem_id: "p_9b1d",
  knowledge_tags: ["一元一次方程", "应用题"],
  problem: {
    stem: "一块农田的总面积为 $x$ 亩，其中水田占总面积的一半多 12 亩，旱田占水田的 $\\frac{2}{5}$。若水田和旱田的总面积等于农田总面积，求 $x$ 的值。",
    givens: [
      "总面积 = $x$",
      "水田 = $\\frac{1}{2}x + 12$",
      "旱田 = $\\frac{2}{5} \\times$ 水田",
      "水田 + 旱田 = 总面积"
    ],
    asks: ["求 $x$ 的值"]
  },
  steps: [
    {
      step_id: "s1",
      title: "理解题意与设元",
      display_markdown: "首先，我们需要明确题目中各个量之间的关系。题目已经设总面积为 $x$ 亩。\n\n根据题意，我们可以直接写出：\n- **总面积**：$x$ 亩",
      speak_text: "首先，我们来看一下题目。题目已经帮我们设好了未知数，总面积就是 x 亩。我们要做的，就是把其他的量都用 x 表示出来。",
      highlights: [
        { type: "key", text: "设总面积为 x" }
      ],
      is_done: true
    },
    {
      step_id: "s2",
      title: "表示水田面积",
      display_markdown: "题目中说“水田占总面积的一半多 12 亩”。\n\n这句话可以翻译为代数式：\n\n$$ \\text{水田} = \\frac{1}{2}x + 12 $$\n\n<mark data-type=\"key\">注意：</mark>“多12”就是加12。",
      speak_text: "接下来我们看水田。题目说水田占总面积的一半多12亩。一半就是二分之一 x，多12就是加12。所以水田的面积就是二分之一 x 加 12。",
      highlights: [
        { type: "key", text: "水田 = 1/2x + 12" }
      ],
      is_done: true
    },
    {
      step_id: "s3",
      title: "表示旱田面积",
      display_markdown: "接下来表示旱田。题目说“旱田占水田的 $\\frac{2}{5}$”。\n\n这意味着旱田面积是水田面积的 $\\frac{2}{5}$ 倍。我们将上一步得到的水田表达式代入：\n\n$$ \\text{旱田} = \\frac{2}{5} \\times (\\text{水田}) $$\n\n即：\n$$ \\text{旱田} = \\frac{2}{5} \\left( \\frac{1}{2}x + 12 \\right) $$\n\n<mark data-type=\"pitfall\">易错点：</mark>代入多项式时，必须加括号！",
      speak_text: "这一步很关键。题目说旱田是水田的五分之二。注意哦，这里是水田的五分之二，不是总面积的。所以我们要用五分之二乘以刚才算出来的水田面积。千万别忘了加括号！",
      highlights: [
        { type: "pitfall", text: "代入必须加括号" }
      ],
      checkpoint: {
        question: "如果不加括号直接写成 $\\frac{2}{5} \\times \\frac{1}{2}x + 12$，这个式子代表什么意思？",
        hint_1: "乘法运算优先级高于加法。",
        hint_2: "如果不加括号，$\\frac{2}{5}$ 只乘到了 $\\frac{1}{2}x$ 上。",
        explanation: "如果不加括号，根据运算优先级，先算乘法，就变成了“水田的一半的2/5，再加上12”，这与题意不符。我们要的是“整个水田”的2/5。",
        type: "mcq",
        options: [
          "旱田的面积",
          "水田的 2/5 加上 12 亩",
          "农田总面积的一半"
        ],
        answer_index: 1
      },
      is_done: false
    },
    {
      step_id: "s4",
      title: "建立方程模型",
      display_markdown: "最后，利用等量关系“水田和旱田的总面积等于农田总面积”列出方程。\n\n$$ (\\text{水田}) + (\\text{旱田}) = \\text{总面积} $$\n\n代入各自的表达式：\n\n$$ \\left(\\frac{1}{2}x + 12\\right) + \\frac{2}{5}\\left(\\frac{1}{2}x + 12\\right) = x $$",
      speak_text: "量都表示出来了，最后一步就是列方程。水田加旱田等于总面积。把刚才那两坨式子加起来，等于 x，方程就列好了。",
      highlights: [
        { type: "result", text: "列出方程" }
      ],
      is_locked: true
    },
    {
      step_id: "s5",
      title: "求解方程",
      display_markdown: "解这个方程：\n\n先提公因式 $(\\frac{1}{2}x + 12)$ 更简单：\n$$ (1 + \\frac{2}{5})(\\frac{1}{2}x + 12) = x $$\n$$ \\frac{7}{5}(\\frac{1}{2}x + 12) = x $$\n\n两边同乘 5：\n$$ 7(\\frac{1}{2}x + 12) = 5x $$\n$$ \\frac{7}{2}x + 84 = 5x $$\n$$ 84 = 5x - 3.5x $$\n$$ 84 = 1.5x $$\n$$ x = 56 $$\n\n<mark data-type=\"result\">最终答案：x = 56</mark>",
      speak_text: "解这个方程有个技巧，不要急着拆括号。你可以把水田面积看成一个整体，提公因式。算出 x 等于 56。",
      highlights: [
        { type: "result", text: "x = 56" }
      ],
      is_locked: true
    }
  ],
  final_answer: "56",
  summary: {
    key_points: ["设元", "整体代入", "提公因式解方程"],
    pitfalls: ["代入多项式漏加括号"],
    one_liner: "关键在于理清部分与整体的关系，并正确使用括号进行代数式变形。"
  }
};
